import { useEffect, useRef } from 'react';
import type { PermissionState } from './use-permissions.js';

/**
 * Minimal per-permission shape the strict-grant predicate needs. `RawPermissionsModal`'s
 * `PermissionEntry[]` is structurally assignable to this, so the hook stays decoupled
 * from the modal's full entry type (think-in-systems: one focused primitive).
 */
export interface GrantPermissionStatus {
  /** Resolved OS permission state; `allowed` only once actually granted. */
  permission?: PermissionState;
  /** True while this permission's status query / request mutation is in flight. */
  loading: boolean;
  /**
   * Continue-gate class carried over from the modal's `PermissionEntry`. `canIgnore` marks
   * an OPTIONAL permission (e.g. launch-at-startup) the user need not grant to proceed.
   * Omitted → treated as required, so callers that already pass a pre-filtered required set
   * (e.g. add-device's `useRequirePermissions`) keep the original strict semantics.
   */
  continueCondition?: 'canIgnore' | 'mustInteract' | 'mustAccept';
}

/**
 * A permission counts toward "success" only when the user must act on it. `canIgnore`
 * permissions are optional and never block a grant.
 *
 * Why this matters (ENG-4307 BLOCKER-1): the onboard modal opens with the WHOLE permission
 * array (no `requiredPermissions` prop), which includes the optional `startup` row
 * (`continueCondition: 'canIgnore'`). Its state derives from
 * `getLoginItemSettings().openAtLogin`, which is only ever set by an explicit user toggle
 * (never auto-enabled), so for the common user it stays `'default'`. Counting it as
 * required made the strict predicate false forever on the primary onboarding flow, so
 * `permissions_grant_succeeded` — this PR's headline metric — never fired for a user who
 * granted camera/mic but left launch-at-startup off.
 */
const isRequiredPermission = (p: GrantPermissionStatus): boolean =>
  p.continueCondition !== 'canIgnore';

/**
 * Strict "all required permissions granted" predicate for the success terminal.
 *
 * Deliberately NOT the loose `allRequiredPermissionsAllowed` continue-gate boolean in
 * permissions.tsx: that treats a `canIgnore` permission as always-true and a
 * `mustInteract` permission as satisfied when merely `!== 'default'` — i.e. TRUE even on
 * a DENIAL, and true at mount for a returning user whose OS grants persist. Wiring
 * `permissions_grant_succeeded` to it would mint false successes and pollute the funnel.
 * A grant succeeds only when every REQUIRED permission is literally `allowed`; optional
 * (`canIgnore`) entries are excluded so leaving launch-at-startup off does not block it.
 * A set with no required permission is never a success (empty `.every` must not read true).
 */
export const areAllPermissionsGranted = (
  permissions: readonly GrantPermissionStatus[]
): boolean => {
  const required = permissions.filter(isRequiredPermission);
  return required.length > 0 && required.every((p) => p.permission === 'allowed');
};

/** True once every required permission has finished loading (guards the flicker below). */
const areAllPermissionsLoaded = (permissions: readonly GrantPermissionStatus[]): boolean =>
  permissions.length > 0 && permissions.every((p) => !p.loading);

/**
 * Fire `onGrantSucceeded` exactly once per modal instance — only on a REAL in-session
 * transition INTO the strict all-`allowed` state. Never on a modal that opens
 * already-satisfied, and never on a denial.
 *
 * The "observed unsatisfied WHILE LOADED" gate is what separates a genuine grant from a
 * returning user's persisted OS grants: `use-permissions.ts` resolves
 * `state = data ? 'allowed' : interacted ? 'denied' : 'default'`, so a persisted grant
 * transitions `default → allowed` during the initial query load. Counting that pre-load
 * `default` as "unsatisfied" would emit a false success on mount, so an unsatisfied state
 * only counts once every permission has finished loading.
 */
export const useGrantSuccessTracking = (
  permissions: readonly GrantPermissionStatus[],
  onGrantSucceeded: () => void
): void => {
  const hasEmittedRef = useRef(false);
  const observedUnsatisfiedWhileLoadedRef = useRef(false);
  // Keep the latest callback without making it an effect dependency, so a fresh closure
  // each render (the modal rebuilds it) cannot re-run the effect on its own.
  const onGrantSucceededRef = useRef(onGrantSucceeded);
  onGrantSucceededRef.current = onGrantSucceeded;

  useEffect(() => {
    // While anything is still loading, the states are not yet meaningful — neither an
    // emit nor an "unsatisfied" observation.
    if (!areAllPermissionsLoaded(permissions)) {
      return;
    }

    if (!areAllPermissionsGranted(permissions)) {
      // A loaded, not-yet-all-granted state: remember it so a later flip to all-granted
      // reads as a real in-session grant rather than an already-satisfied mount.
      observedUnsatisfiedWhileLoadedRef.current = true;
      return;
    }

    // Fully loaded AND all granted. Emit only for a real transition, once.
    if (hasEmittedRef.current || !observedUnsatisfiedWhileLoadedRef.current) {
      return;
    }

    hasEmittedRef.current = true;
    onGrantSucceededRef.current();
  }, [permissions]);
};
